import React, { useState, useEffect } from 'react';

function CustomerLicenses({ customer }) {
  const [licenses, setLicenses] = useState([]);
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [formData, setFormData] = useState({
    LicenseID: '',
    StartDate: '',
    ExpiryDate: '',
    Type: '',
    IsActive: false,
  });

  // Fetch licenses for the given customer
  useEffect(() => {
    if (customer) {
      fetchLicenses(customer.CustomerID);
    }
  }, [customer]);

  const fetchLicenses = (customerId) => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customerId}/licenses`)
      .then((res) => res.json())
      .then((data) => setLicenses(data))
      .catch((err) => console.error('Failed to fetch licenses:', err));
  };

  // Fetch all license types once
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/license-types`)
      .then((res) => res.json())
      .then((data) => setLicenseTypes(data))
      .catch((err) => console.error('Failed to fetch license types:', err));
  }, []);

    // Helper function to get the friendly display name for a license type
    const getLicenseDisplayName = (licenseID) => {
      const licenseType = licenseTypes.find((lt) => lt.LicenseID === licenseID);
      return licenseType ? licenseType.DisplayName : 'Unknown License Type';
    };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle form submission to add a new license
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!customer) return;

    // Validate Start and End Dates for licenses
    if (!formData.StartDate || !formData.ExpiryDate) {
      alert('Both Start Date and Expiry Date are required.');
      return;
    }

    const startDate = new Date(formData.StartDate);
    const expiryDate = new Date(formData.ExpiryDate);

    if (expiryDate <= startDate) {
      alert('Expiry Date must be later than Start Date.');
      return;
    }

    const licensePayload = {
      ...formData,
      LicenseID: parseInt(formData.LicenseID, 10),
    };

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customer.CustomerID}/licenses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(licensePayload),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to add license');
        }
        return res.json();
      })
      .then(() => {
        // Refresh the license list
        fetchLicenses(customer.CustomerID);
        // Reset the form
        setFormData({
          LicenseID: '',
          StartDate: '',
          ExpiryDate: '',
          Type: '',
        });
      })
      .catch((err) => {
        console.error(err);
        alert('Error adding license.');
      });
  };

  // Deactivate a license
  const handleDeactivateLicense = (licenseRecordId) => {
    if (!customer) return;

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customer.CustomerID}/licenses/${licenseRecordId}/deactivate`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to deactivate license');
        }
        return res.json();
      })
      .then(() => {
        // Refresh the license list
        fetchLicenses(customer.CustomerID);
      })
      .catch((err) => {
        console.error(err);
        alert('Error deactivating license.');
      });
  };

  // Remove a license
  const handleRemoveLicense = (licenseRecordId) => {
    if (!customer) return;

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customer.CustomerID}/licenses/${licenseRecordId}`, {
      method: 'DELETE',
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to remove license');
        }
        return res.json();
      })
      .then(() => {
        // Refresh the license list
        fetchLicenses(customer.CustomerID);
      })
      .catch((err) => {
        console.error(err);
        alert('Error removing license.');
      });
  };

  return (
    <div>
      <h2>Licenses for {customer.CompanyName}</h2>

      {/* Existing licenses list */}
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {licenses.map((lic) => (
          <li key={lic.Id} style={{ marginBottom: '8px' }}>
            <strong>License Type:</strong> {getLicenseDisplayName(lic.LicenseID)},{' '}
            <strong>Type:</strong> {lic.Type},{' '}
            <strong>Active:</strong> {lic.IsActive ? 'Yes' : 'No'},{' '}
            <strong>Start:</strong> {lic.StartDate?.split('T')[0]},{' '}
            <strong>Expiry:</strong> {lic.ExpiryDate?.split('T')[0]}
            {/* Deactivate and Remove buttons */}
            <div>
              {lic.IsActive && (
                <button
                  style={{ marginRight: '10px' }}
                  onClick={() => handleDeactivateLicense(lic.Id)}
                >
                  Deactivate
                </button>
              )}
              <button
                style={{ backgroundColor: 'red', color: '#fff' }}
                onClick={() => handleRemoveLicense(lic.Id)}
              >
                Remove
              </button>
            </div>
          </li>
        ))}
      </ul>
      <br />
      <hr />
      {/* Form to add a new license */}
      <h3>Add New License</h3>
      <form onSubmit={handleSubmit}>
        <div>
          <label>License Type: </label>
          <select
            name="LicenseID"
            value={formData.LicenseID}
            onChange={handleChange}
            required
          >
            <option value="">-- Select License Type --</option>
            {licenseTypes.map((lt) => (
              <option key={lt.LicenseID} value={lt.LicenseID}>
                {lt.DisplayName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Start Date: </label>
          <input
            type="date"
            name="StartDate"
            value={formData.StartDate}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label>Expiry Date: </label>
          <input
            type="date"
            name="ExpiryDate"
            value={formData.ExpiryDate}
            onChange={handleChange}
            required
            min={formData.StartDate ? formData.StartDate : undefined}
          />
        </div>

        <div>
          <label>Type: </label>
          <input
            type="text"
            name="Type"
            value={formData.Type}
            onChange={handleChange}
          />
        </div>

        <button type="submit">Add License</button>
      </form>
    </div>
  );
}

export default CustomerLicenses;
